import React, { useState } from 'react';
import { FileText, Shield, Clock, AlertTriangle, ShieldCheck, Layers, Gem, Star, Wallet, CalendarDays, Calendar, Calendar1, Crown, ChartPie, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
// Reuse the Broker dashboard's StatsCard so the insurer metric cards are visually identical.
import { StatsCard } from '../../../shared/dashboard/broker/components/StatsCard';
import SelectMenu from '../../../../components/form-control/SelectMenu';
import brokerStyles from '../../../shared/dashboard/broker/BrokerDashboard.module.css';
import { RegionMetric } from './components/MockInsuranceData';
import { useQuery } from '@tanstack/react-query';
import { getBranchPerformance, getInsurerDashboardMetricsSummary, getPolicyDistribution, getSingleInsurerDashboardMetrics, getTeamDashboardMetrics } from '../../../../api/requests';
import { ITeamAdminMetrics } from '../../../../models/generalMetrics/IGeneralMetrics';
import { ITeamMetricsDTO } from '../../../../resources/interfaces';
import { SelectedDateRange } from '../../../../components/dashboard/date-range-selector/datePicker';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import { faker } from '@faker-js/faker';
import useUserAccount from '../../../../hooks/userAccountHook';
import useRoute from '../../../../hooks/routesHook';
import { YearRangeSelector } from '../../../../components/dashboard/date-range-selector/RangeSelector';
import useVehicleVerification from '../../../../hooks/vehicleInspectionHook';
import { formatCurrencyAmount, getGreeting } from '../../../../utils/miscFunctions';
import { faBullseye } from '@fortawesome/free-solid-svg-icons';
import usePremiumFinancing from '../../../../hooks/premiumFinancingHook';
import BrokerLeaderboard from './components/BrokerLeaderboard';
import PolicyDistributionWidget from './components/PolicyDistributionWidget';
import RenewalsWithin30Days from './components/RenewalsWithin30Days';
import AwaitingApproval from './components/AwaitingApproval';
import SetupChecklist from './components/SetupChecklist';
// Trimmed from the insurer dashboard per the dashboard-cleanup spec:
//   - MonthlyChart (Monthly Performance + Products)
//   - BranchPerformance
//   - VerificationMonthlyTrendsChart / VehicleVerificationMonthlyTrendsChart
// PremiumFinancingMonthlyTrendsChart is "hide for now" — kept commented so
// it can be uncommented in one place when re-enabled.
// import MonthlyChart from './components/MonthlyChart';
// import BranchPerformance from './components/BranchPerformance';
// import VerificationMonthlyTrendsChart from '../../../shared/vehicle-verification/VerificationMonthlyTrendsChart';
// import VehicleVerificationMonthlyTrendsChart from '../../../shared/vehicle-verification/VerificationMonthlyTrendsChart';
// import PremiumFinancingMonthlyTrendsChart from '../../../shared/premiumFinancing/MonthlyTrendsChart';

interface InsuranceAdminDashboardProps {
  userName?: string;
  isCompany?: boolean;
  id?: number;
}


const InsurerManagementDashboard: React.FC<InsuranceAdminDashboardProps> = ({ userName, isCompany = false, id }) => {
  const { fetchVehicleVerifiactionMetrics } = useVehicleVerification();
  const [metricsSummary, setMetricsSummary] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedCountry = useSelector(getSelectedCountry);
  const countryId = selectedCountry?.id ?? 1
  const { user } = useUserAccount()
  const { getDashboardBase } = useRoute()
  const companyId = user?.company?.id
  const [pieData, setPieData] = useState<any>()
  const [insurerMetrics, setInsurerMetrics] = useState<any>()
  const [insurerBranchPerformance, setInsurerBranchPerformance] = useState<any>()
  const { fetchPremiumFinancingMetrics } = usePremiumFinancing();
  const currentDate = new Date();
  const startDate = moment(currentDate).format('YYYY-MM-DD');

  const [selectedDateRange, setSelectedDateRange] = useState<SelectedDateRange>({
    from: '',
    to: ''
  });
  const [splineChartRange, setSplineChartRange] = useState({
    quarter: 'Q1',
    year: moment().format('YYYY')
  })
  const [generalMetrics, setGeneralMetrics] = useState<ITeamAdminMetrics>({
    mom: {
      count: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      },
      sales: {
        JANUARY: 0,
        FEBRUARY: 0,
        MARCH: 0,
        APRIL: 0,
        MAY: 0,
        JUNE: 0,
        JULY: 0,
        AUGUST: 0,
        SEPTEMBER: 0,
        OCTOBER: 0,
        NOVEMBER: 0,
        DECEMBER: 0,
      }
    },
    sales: {
      this_month: 0,
      today: 0,
      this_week: 0,
      this_year: 0
    },
    products: {
      'Auto Insurance Sales': 0,
      'Home Insurance Sales': 0
    },
    salesMix: {
      amount: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      },
      count: {
        comprehensive_90: 0,
        comprehensive_85: 0,
        comprehensive_100: 0,
        third_party_ft: 0,
        third_party: 0
      }
    },
    signups: {
      categories: {
        comprehensive: 0,
        third_party: 0
      }

    }
  });


  useQuery({ queryKey: ['getPerformance',], queryFn: () => getPerformance() })
  useQuery({ queryKey: ['generalMetricsProductSales', generalMetrics], queryFn: () => generalMetricsProductSales() });
  useQuery({ queryKey: ['getTeamMetricsData', selectedDateRange, splineChartRange, countryId], queryFn: () => getTeamMetricsData() });
  useQuery({ queryKey: ['generalMetricsSalesMix', generalMetrics], queryFn: () => generalMetricsSalesMix() });
  const { isLoading: isLoadingInsurerMetrics } = useQuery({ queryKey: ['getInsurerMetrics', id, splineChartRange.year], queryFn: () => getInsurerMetrics() })
  useQuery({ queryKey: ['getSingleInsurerMetrics'], queryFn: () => getSingleInsurerMetrics() })
  useQuery({ queryKey: ['getPieChartData'], queryFn: () => getPieChartData() })

  const { data: vehicleVerificationMetrics, isLoading: vehicleVerificationMetricsLoading } = useQuery({
    queryKey: ['fetchVehicleVerificationMetrics', companyId, selectedDateRange],
    queryFn: () => fetchVehicleVerifiactionMetrics(companyId, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !!companyId
  })

  const { data: premiumFinancingMetrics, isLoading: premiumFinancingMetricsLoading } = useQuery({
    queryKey: ['fetchPremiumFinancingMetrics', companyId, selectedDateRange],
    queryFn: () => fetchPremiumFinancingMetrics(companyId, selectedDateRange?.from, selectedDateRange?.to),
    enabled: !!companyId
  })


  // These dashboard metric fns are side-effect fetches (they setState and
  // return nothing). Used directly as React Query queryFns they resolved to
  // undefined, which React Query v5 rejects ("Query data cannot be undefined").
  // Return null so the query is valid; the setState still updates the UI.
  const getPerformance = () => {
    getBranchPerformance(startDate, startDate)
      .then((response: any) => {
        setInsurerBranchPerformance(response?.data?.data)
      })
      .catch((error: Error) => {
        console.log("error", error)
      })
      .finally(() => { })
    return null
  }

  const handleSplineChartDateRangeChange = (dateRange: { quarter: string; year: string }) => {
    setSplineChartRange(dateRange);
  };

  const [salesMix, setSalesMix] = useState({
    amount: {
      "Auto Com": 0,
      'Auto TP': 0
    },
    count: {
      "Auto Com": 0,
      'Auto TP': 0
    }
  });

  const [productSales, setproductsales] = useState({
    'Auto Policies': 0,
    'Home Policies': 0
  });

  const generalMetricsProductSales = () => {
    if (generalMetrics.products) {
      setproductsales({
        ...productSales,
        'Auto Policies': generalMetrics.products['Auto Insurance Sales'] ?? 0,
        'Home Policies': generalMetrics.products['Home Insurance Sales'] ?? 0
      });
    }
    return null
  }

  const generalMetricsSalesMix = () => {
    if (generalMetrics.salesMix) {
      setSalesMix({
        ...salesMix,
        amount: {
          "Auto Com":
            generalMetrics?.salesMix?.amount?.comprehensive_100 +
            generalMetrics?.salesMix?.amount?.comprehensive_90 +
            generalMetrics?.salesMix?.amount?.comprehensive_85,
          'Auto TP':
            generalMetrics?.salesMix?.amount?.third_party +
            generalMetrics?.salesMix?.amount?.third_party_ft
        },
        count: {
          "Auto Com":
            generalMetrics?.salesMix?.count?.comprehensive_100 +
            generalMetrics?.salesMix?.count?.comprehensive_90 +
            generalMetrics?.salesMix?.count?.comprehensive_85,
          'Auto TP':
            generalMetrics?.salesMix?.count?.third_party +
            generalMetrics?.salesMix?.count?.third_party_ft
        }
      });
    }
    return null
  }

  const getTeamMetricsData = () => {
    setLoading(true);

    const params: ITeamMetricsDTO = { ...selectedDateRange, year: splineChartRange.year }
    getTeamDashboardMetrics(countryId, params)
      .then((res) => setGeneralMetrics(res))
      .catch((error: Error) => { })
      .finally(() => {
        setLoading(false);
      });
    return null
  };

  const getPieChartData = async () => {
    await getPolicyDistribution(id)
      .then((res: any) => {
        setPieData(res.data)
      })
      .catch((ror: Error) => { console.log("ror", ror) })
      .finally(() => setLoading(false))
    return null
  }

  const getInsurerMetrics = async () => {
    await getInsurerDashboardMetricsSummary(id, splineChartRange.year)
      .then((res: any) => {
        setInsurerMetrics(res.data)
      })
      .catch((ror: Error) => {
        console.log(ror)
      })
      .finally(() => { })
    return null
  }

  const getSingleInsurerMetrics = async () => {
    await getSingleInsurerDashboardMetrics(id)
      .then((res) => {
      })
      .catch((ror: Error) => console.log(ror))
      .finally(() => { })
    return null
  }

  // Insurer financial metric cards — mirrors the Broker dashboard layout (Policy Count / Premium,
  // YTD on the first row and MTD on the second). The broker's Commission cards have no insurer
  // equivalent, so they are intentionally omitted.
  // Values come from the insurer summary endpoint, which returns the same YTD/MTD keys the broker
  // uses (policyCountYtd/Mtd, yearToDateSales, monthlySales) scoped to the insurer's own policies.
  const currentMonthLabel = moment().format('MMM');
  const policyCountYtd = insurerMetrics?.policyCountYtd ?? 0;
  const policyCountMtd = insurerMetrics?.policyCountMtd ?? 0;
  const premiumYtd = insurerMetrics?.yearToDateSales ?? 0;
  const premiumMtd = insurerMetrics?.monthlySales ?? 0;
  // Total Policy Count split by entry channel (Online Entry vs Bulk Upload).
  const policyCountYtdOnline = insurerMetrics?.policyCountYtdOnline ?? 0;
  const policyCountYtdBulk = insurerMetrics?.policyCountYtdBulk ?? 0;
  const policyCountMtdOnline = insurerMetrics?.policyCountMtdOnline ?? 0;
  const policyCountMtdBulk = insurerMetrics?.policyCountMtdBulk ?? 0;
  // Commission the insurer still has to settle to brokers.
  const commissionToSettleYtd = insurerMetrics?.commissionToSettleYtd ?? 0;
  const commissionToSettleMtd = insurerMetrics?.commissionToSettleMtd ?? 0;

  const channelSubText = (online: number, bulk: number) => (
    <span>Online Entry: {online} · Bulk Upload: {bulk}</span>
  );

  const insurerFinancialStats = [
    {
      title: 'Total Policy Count YTD',
      value: policyCountYtd,
      icon: FileText,
      color: 'blue' as const,
      period: splineChartRange.year,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      subText: channelSubText(policyCountYtdOnline, policyCountYtdBulk),
      className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50'
    },
    {
      title: 'Premium YTD',
      value: premiumYtd,
      icon: TrendingUp,
      color: 'green' as const,
      period: splineChartRange.year,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50'
    },
    {
      title: 'Commission to Settle YTD',
      value: commissionToSettleYtd,
      icon: Wallet,
      color: 'gold' as const,
      period: splineChartRange.year,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      className: 'bg-[#FFFBE6] text-[#CCAC00] border-[#FFE58F] dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50'
    },
    {
      title: 'Total Policy Count MTD',
      value: policyCountMtd,
      icon: FileText,
      color: 'blue' as const,
      period: currentMonthLabel,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      subText: channelSubText(policyCountMtdOnline, policyCountMtdBulk),
      className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50'
    },
    {
      title: 'Premium MTD',
      value: premiumMtd,
      icon: TrendingUp,
      color: 'green' as const,
      period: currentMonthLabel,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50'
    },
    {
      title: 'Commission to Settle MTD',
      value: commissionToSettleMtd,
      icon: Wallet,
      color: 'gold' as const,
      period: currentMonthLabel,
      isClickable: false,
      loading: isLoadingInsurerMetrics,
      className: 'bg-[#FFFBE6] text-[#CCAC00] border-[#FFE58F] dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50'
    },
  ];

  return (
    <div className={brokerStyles.dashboardContainer}>
      {/* Personal greeting — hidden when an admin is viewing a specific insurer company (isCompany). */}
      {!isCompany && (
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {getGreeting()}, {user?.firstName ? `${user.firstName}` : ''}
          </h1>
          <div className="text-gray-600 dark:text-gray-400 grid grid-cols-2 w-full">
            {"Get a real-time view of your policies and financials and activities. Manage your portfolio, track key metrics, and take action—all from one place"}
          </div>
        </div>
      )}

      {/* Setup Checklist — shown to insurers viewing their own dashboard until all
          steps are complete (or dismissed). Replaces the old forced-onboarding wizard. */}
      {!isCompany && user?.isInsurer() && <SetupChecklist />}

      {/* Financial Metrics — mirrors the Broker dashboard's metric-card container and year filter. */}
      <div className={`${brokerStyles.bannerSection} dark:bg-darkBg dark:border-darkBg-border`}>
        <div className="p-5 border-b border-gray-100 dark:border-darkBg-border flex md:flex-row flex-col justify-between md:items-center gap-5">
          <div className='flex items-center md:justify-center gap-4'>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-darkBg-mainText">Financial Metrics</h2>
            <div className="w-40">
              <SelectMenu
                label=""
                name="years"
                listSelectedValue={splineChartRange.year}
                onChange={(val: string) => {
                  handleSplineChartDateRangeChange({ ...splineChartRange, year: val })
                }}
                list={['2024', '2025', '2026']}
              />
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 p-5">
          {insurerFinancialStats.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>
      </div>

      {/* Broker leaderboard, policy distribution & upcoming renewals.
          The Renewals card wraps in a Link to /renewals so clicking anywhere
          on it deep-links into the full insurer Renewals page. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <BrokerLeaderboard companyId={id ?? (companyId != null ? Number(companyId) : undefined)} />
        <PolicyDistributionWidget
          labels={pieData?.labels ?? []}
          policyCount={pieData?.policyCount ?? []}
          premium={pieData?.premium ?? []}
          loading={loading}
        />
        <Link
          to={`${getDashboardBase()}/renewals`}
          className="block hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main rounded-2xl"
        >
          <RenewalsWithin30Days companyId={id ?? (companyId != null ? Number(companyId) : undefined)} />
        </Link>
      </div>

      {/* Awaiting your approval — policies brokers have submitted for this
          insurer. Navigation is handled inside the card: the heading (and
          "View all") link to the full approval queue on the Policies page,
          while each row links to that policy's detail page. Do NOT pass
          companyId as insurerId: the queue filters by the insurer *entity* id
          (policy.insurerId), not the company id, and company.id !== insurer.id.
          On the insurer's own dashboard we omit it so the API's security filter
          scopes correctly; `id` stays as the explicit admin-inspect override. */}
      <AwaitingApproval insurerId={id} />

      {/* Removed per the dashboard-cleanup spec:
            - MonthlyChart (Monthly Performance + Products)
            - BranchPerformance
            - VerificationMonthlyTrendsChart (Verification Overview)
            - PolicyDistributionWidget (above)
          PremiumFinancingMonthlyTrendsChart is "hide for now" — block kept
          commented so it can be re-enabled in one place when needed.
      */}
      {/*
      <PremiumFinancingMonthlyTrendsChart
        isLoading={premiumFinancingMetricsLoading}
        data={premiumFinancingMetrics?.monthlyTrend || []}
        title="Premium Financing Monthly Trends"
        description="Showing count data by month"
      />
      */}
    </div>
  );
};

export default InsurerManagementDashboard;

export const generateRegionMetrics = (): RegionMetric[] => {
  const regions = ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'Northwest', 'Central'];

  return regions.map(region => ({
    region,
    policies: faker.number.int({ min: 2000, max: 10000 }),
    premium: faker.number.int({ min: 1000000, max: 5000000 }),
    claims: faker.number.int({ min: 100, max: 2000 }),
    growth: faker.number.float({ min: -2, max: 15 })
  }));
};

interface keyPerformanceIndicatorItemProps {
  title: string;
  value: number | string;
  children?: JSX.Element | undefined;
  icon: JSX.Element;
  iconClasses?: string;
}
export const KeyPerformanceIndicatorItem = ({ title, value, children, icon, iconClasses }: keyPerformanceIndicatorItemProps) => {
  return (
    <div className="bg-gray-50 p-4 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border overflow-hidden">
      <div className='flex items-center gap-3'>
        <div className={`${iconClasses} w-8 h-8 rounded-md flex items-center justify-center`}>
          {icon}
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-darkBg-mainText">{title}</p>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-darkBg-mainText">{value}</p>
        </div>
      </div>


      {children && <div className="">
        {children}
      </div>}
    </div>
  )
}